import { ProjectRole } from '@/features/projects/shared/types/project-role';
import { supabase } from '@/lib/supabase';
import { DocumentVisibility } from '@/shared/components/ui/upload/document-upload-dialog';
import { SecureProjectDocument } from '@/shared/types/api';

export interface DocumentFilters {
  fromDate?: Date;
  toDate?: Date;
  projectIds?: string[];
  partnerIds?: string[];
  roles?: ProjectRole[];
  uploadedBy?: string[];
  visibility?: DocumentVisibility[];
  searchQuery?: string;
}

interface GetDocumentsParams {
  p_organization_id?: number;
  p_project_id?: number;
  p_file_type?: string;
  p_from_date?: string;
  p_to_date?: string;
  p_visibilities?: string[];
  p_project_ids?: number[];
  p_partner_ids?: string[];
  p_roles?: string[];
  p_uploaded_by?: string[];
  p_search_query?: string;
}

export const getDocumentsForProject = async (
  projectId: number,
  filters?: DocumentFilters,
): Promise<SecureProjectDocument[] | null> => {
  const params: GetDocumentsParams = {
    p_project_id: projectId,
    p_from_date: filters?.fromDate?.toISOString(),
    p_to_date: filters?.toDate?.toISOString(),
    p_visibilities: filters?.visibility as unknown as string[] | undefined,
    p_partner_ids: filters?.partnerIds,
    p_roles: filters?.roles as unknown as string[] | undefined,
    p_uploaded_by: filters?.uploadedBy,
    p_search_query: filters?.searchQuery,
  };

  const { data, error } = await supabase.rpc('get_documents', params);

  if (error) {
    throw error;
  }

  // Cast to include new fields that may not be in auto-generated types yet
  return data.map((document) => {
    const doc = document as typeof document & {
      project_type?: string;
      client_org_name?: string;
    };
    return {
      ...doc,
      creator: doc.creator as { id: string; name: string },
      project_name: doc.project_name,
      sub_project_names: doc.sub_project_names,
      partner_names: doc.partner_names,
      role: doc.role,
      uploaded_by_org: doc.uploaded_by_org,
      project_type: (doc.project_type ??
        'project') as SecureProjectDocument['project_type'],
      client_org_name: doc.client_org_name ?? null,
    };
  });
};

export const getDocumentsForOrganization = async (
  orgId: number,
  filters?: DocumentFilters,
): Promise<SecureProjectDocument[] | null> => {
  const params: GetDocumentsParams = {
    p_organization_id: orgId,
    p_from_date: filters?.fromDate?.toISOString(),
    p_to_date: filters?.toDate?.toISOString(),
    p_project_ids: filters?.projectIds?.map((id) => Number(id)).filter(Boolean),
    p_visibilities: filters?.visibility as unknown as string[] | undefined,
    p_partner_ids: filters?.partnerIds,
    p_roles: filters?.roles as unknown as string[] | undefined,
    p_uploaded_by: filters?.uploadedBy,
    p_search_query: filters?.searchQuery,
  };

  const { data, error } = await supabase.rpc('get_documents', params);

  if (error) {
    throw error;
  }

  // Cast to include new fields that may not be in auto-generated types yet
  return data.map((document) => {
    const doc = document as typeof document & {
      project_type?: string;
      client_org_name?: string;
    };
    return {
      ...doc,
      creator: doc.creator as { id: string; name: string },
      project_name: doc.project_name,
      sub_project_names: doc.sub_project_names,
      partner_names: doc.partner_names,
      role: doc.role,
      uploaded_by_org: doc.uploaded_by_org,
      project_type: (doc.project_type ??
        'project') as SecureProjectDocument['project_type'],
      client_org_name: doc.client_org_name ?? null,
    };
  });
};
